// src/models/logModel.js - Add formatCurrency function
import api from '../services/api';




// Utility function to format currency
export const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    }).format(amount);
};

// Get all activity logs
export const getAllLogs = async (filters = {}) => {
    try {
        const response = await api.get('/logs', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching activity logs' };
    }
};

// Get logs by entity type (tenant, payment, building, etc.)
export const getLogsByEntityType = async (entityType, filters = {}) => {
    try {
        const response = await api.get('/logs', { params: { ...filters, entityType } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: `Error fetching ${entityType} logs` };
    }
};

// Get tenant activity logs
export const getTenantLogs = async (filters = {}) => {
    return getLogsByEntityType('tenant', filters);
};

// Get payment activity logs
export const getPaymentLogs = async (filters = {}) => {
    return getLogsByEntityType('payment', filters);
};

// Get user activity logs
export const getUserActivityLogs = async (userId, filters = {}) => {
    try {
        const response = await api.get('/logs/user', { params: { ...filters, userId } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching user activity logs' };
    }
};

// Get logs by entity ID (specific tenant, payment, etc.)
export const getLogsByEntityId = async (entityType, entityId, filters = {}) => {
    try {
        const response = await api.get('/logs/entity', { params: { ...filters, entityType, entityId } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: `Error fetching logs for ${entityType} ID: ${entityId}` };
    }
};

// Get monthly revenue with user attribution
export const getMonthlyRevenueByUser = async (year, month) => {
    try {
        const response = await api.get('/logs/revenue/users', { params: { year, month } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching monthly revenue by user' };
    }
};

// Export logs to Excel
export const exportLogsToExcel = async (filters = {}) => {
    try {
        const response = await api.get('/logs/export', {
            params: filters,
            responseType: 'blob'
        });

        // Create a download link
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const downloadUrl = window.URL.createObjectURL(blob);

        // Create a temporary link and trigger the download
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        a.remove();

        return { success: true, message: 'Logs exported successfully' };
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error exporting logs to Excel' };
    }
};

// Helper functions
export const formatLogDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(date);
};

export const getActionColor = (action, isDarkMode = false) => {
    const colors = {
        'create': isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-700',
        'update': isDarkMode ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-700',
        'delete': isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-700',
        'payment': isDarkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-700',
        'record_payment': isDarkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-700',
        'login': isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700',
        'logout': isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700',
        'login_success': isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700',
        'logout_success': isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700',
        'view_payment_logs': isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700',
        'view_activity_logs': isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700',
        'view_revenue_report': isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700',
        'view_occupancy_report': isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700',
        'view_tenant_logs': isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700'
    };

    return colors[action] || (isDarkMode ? 'bg-zinc-950/20 text-zinc-400' : 'bg-gray-100 text-gray-700');
};

export const getEntityTypeIcon = (entityType) => {
    const icons = {
        'tenant': 'RiTeamLine',
        'payment': 'RiMoneyDollarCircleLine',
        'building': 'RiBuildingLine',
        'apartment': 'RiHomeHeartLine',
        'owner': 'RiUserLine',
        'user': 'RiShieldUserLine',
        'system': 'RiSettings4Line'
    };

    return icons[entityType] || 'RiFileChartLine';
};

export const formatLogDetails = (details) => {
    if (!details) return '-';

    try {
        // Check if details is a JSON string or contains a JSON string
        let jsonStr = details;
        let prefix = '';

        // Handle cases like "Reports {"method":"GET",...}"
        const jsonMatch = details.match(/^([^{]+)?({.*})$/);
        if (jsonMatch) {
            prefix = (jsonMatch[1] || '').trim();
            jsonStr = jsonMatch[2];
        }

        const data = JSON.parse(jsonStr);

        // Format common log types
        if (data.method && data.url) {
            // API Request logs - Don't show URL/Method per user request
            // instead, show important params or body
            const url = new URL(data.url, 'http://localhost');
            const params = Object.fromEntries(url.searchParams.entries());

            const parts = [];

            // Add parameters if any (excluding common noise)
            Object.entries(params).forEach(([key, value]) => {
                if (!['token', 'apiKey', 'password'].includes(key)) {
                    parts.push(`${key}: ${value}`);
                }
            });

            // Add body if it's an object and has keys
            if (data.body && typeof data.body === 'object' && Object.keys(data.body).length > 0) {
                Object.entries(data.body).forEach(([key, value]) => {
                    if (!['token', 'apiKey', 'password', 'confirmPassword'].includes(key) && typeof value !== 'object') {
                        parts.push(`${key}: ${value}`);
                    }
                });
            }

            const formattedDetails = parts.join(', ');
            return prefix ? `${prefix}: ${formattedDetails || '-'}` : (formattedDetails || '-');
        }

        // General JSON formatting
        const formatted = Object.entries(data)
            .filter(([key, value]) => value !== null && value !== undefined && typeof value !== 'object')
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');

        return prefix ? `${prefix}: ${formatted || jsonStr}` : (formatted || jsonStr);
    } catch (e) {
        // Not JSON, return original
        return details;
    }
};

export const LOG_ACTION_TYPES = [
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
    { value: 'payment', label: 'Payment' },
    { value: 'record_payment', label: 'Record Payment' },
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'login_success', label: 'Login Success' },
    { value: 'logout_success', label: 'Logout Success' },
    { value: 'view_payment_logs', label: 'View Payment Logs' },
    { value: 'view_activity_logs', label: 'View Activity Logs' },
    { value: 'view_revenue_report', label: 'View Revenue Report' },
    { value: 'view_occupancy_report', label: 'View Occupancy Report' },
    { value: 'view_tenant_logs', label: 'View Tenant Logs' }
];

export const LOG_ENTITY_TYPES = [
    { value: 'tenant', label: 'Tenant' },
    { value: 'payment', label: 'Payment' },
    { value: 'building', label: 'Building' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'owner', label: 'Owner' },
    { value: 'user', label: 'User' },
    { value: 'system', label: 'System' },
    { value: 'Reports', label: 'Reports' }
];

export default {
    getAllLogs,
    getLogsByEntityType,
    getTenantLogs,
    getPaymentLogs,
    getUserActivityLogs,
    getLogsByEntityId,
    getMonthlyRevenueByUser,
    exportLogsToExcel,
    formatLogDate,
    formatCurrency,
    formatLogDetails,
    getActionColor,
    getEntityTypeIcon,
    LOG_ACTION_TYPES,
    LOG_ENTITY_TYPES
};